import React, {useContext, useEffect, useState} from 'react';
import './results.css';
import {useMergedResults} from '../../../../../hooks/useMergedResults';
import VaccinationsBar from '../vaccinations-bar/VaccinationsBar';
import {VaccineModel} from '../../../../../model/vaccine.model';
import ButtonComponent from '../../../../shared/button/Button.component';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import { Download, ExternalLink } from 'lucide-react';
import VaccinationsBarProfessionals from '../vaccinations-bar-professionals/VaccinationsBarProfessionals';
import ResultsContext from '../../../../../store/results-context';
import {ResultsModel} from '../../../../../model/results.model';
import ContraindicatedVaccinationsBar from '../contraindicated-vaccinations-bar/ContraindicatedVaccinationsBar';
import {PDFDownloadLink} from '@react-pdf/renderer';
import Pdf from '../../../../shared/pdf/Pdf';
import PdfShortened from '../../../../shared/pdf-shortened/PdfShortened';

interface ResultsProps {
    t: (key: string) => string;
}

const Results: React.FC<ResultsProps> = ({t}: ResultsProps) => {

    const navigate: NavigateFunction = useNavigate();

    const resultsCtx: ResultsModel = useContext(ResultsContext);

    const [pdfKey, setPdfKey] = useState(Date.now());

    const showResultsForProfessionals: boolean =
        localStorage.getItem('loginButtonClicked') === 'true' && resultsCtx.showResultsForProfessionals;

    let finalVaccinations: {
        mandatoryVaccinations: VaccineModel[],
        recommendedVaccinations: VaccineModel[],
        highlyRecommendedVaccinations: VaccineModel[],
        contraindicatedVaccinations: VaccineModel[]
    } = useMergedResults();

    const showResults: boolean =
        finalVaccinations.mandatoryVaccinations.length > 0 ||
        finalVaccinations.highlyRecommendedVaccinations.length > 0 ||
        finalVaccinations.recommendedVaccinations.length > 0 ||
        finalVaccinations.contraindicatedVaccinations.length > 0;

    const goBackButtonText: string = t('go_back_button');

    const goBackButtonHandler: () => void = (): void => {
        navigate('/risk-factors');
    }

    const pdfButtonStyle = {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        border: "1px solid var(--black)",
        padding: "20px",
        borderRadius: "10px",
        fontSize: "14px",
        color: "var(--very-light-gray)",
        background: "var(--dark-background)",
        textDecoration: "none",
        justifyContent: "center"
    }

    useEffect(() => {
        setPdfKey(Date.now());
    }, [finalVaccinations]);

    return (
        <div className="results__main-wrapper">

            <div className="results__go-back__wrapper">
                <ButtonComponent id="goBackButton"
                                 value={goBackButtonText}
                                 className="go-back_next__button"
                                 onClick={goBackButtonHandler}/>
            </div>

            {
                !showResults &&
                <div>
                    {t('no_results_info')}
                </div>
            }

            {
                showResults &&
                <div className="results__main-results__wrapper">

                    <div className="results_inner__wrapper">

                        {
                            finalVaccinations.mandatoryVaccinations.length > 0 &&
                            <div className="results_vaccinations-header-bar__wrapper">
                                <div className="results_vaccinations-header__wrapper">
                                    {t('mandatory')}
                                </div>
                                <div className="results_vaccinations-bar__wrapper mandatory-background">
                                    {
                                        showResultsForProfessionals ?
                                            <VaccinationsBarProfessionals t={t}
                                                                          vaccinations={finalVaccinations.mandatoryVaccinations}
                                                                          vaccinationType="mandatory" /> :
                                            <VaccinationsBar t={t}
                                                             vaccinations={finalVaccinations.mandatoryVaccinations}
                                                             vaccinationType="mandatory"/>

                                    }
                                </div>
                            </div>
                        }

                        {
                            finalVaccinations.highlyRecommendedVaccinations.length > 0 &&
                            <div className="results_vaccinations-header-bar__wrapper">
                                <div className="results_vaccinations-header__wrapper">
                                    {t('highly_recommended')}
                                </div>
                                <div className="results_vaccinations-bar__wrapper highly-recommended-background">
                                    {
                                        showResultsForProfessionals ?
                                            <VaccinationsBarProfessionals t={t}
                                                                          vaccinations={finalVaccinations.highlyRecommendedVaccinations}
                                                                          vaccinationType="highly_recommended" /> :
                                            <VaccinationsBar t={t}
                                                             vaccinations={finalVaccinations.highlyRecommendedVaccinations}
                                                             vaccinationType="highly_recommended"/>
                                    }
                                </div>
                            </div>
                        }

                        {
                            finalVaccinations.recommendedVaccinations.length > 0 &&
                            <div className="results_vaccinations-header-bar__wrapper">
                                <div className="results_vaccinations-header__wrapper">
                                    {t('recommended')}
                                </div>
                                <div className="results_vaccinations-bar__wrapper recommended-background">
                                    {
                                        showResultsForProfessionals ?
                                            <VaccinationsBarProfessionals t={t}
                                                                          vaccinations={finalVaccinations.recommendedVaccinations}
                                                                          vaccinationType="recommended" /> :
                                            <VaccinationsBar t={t}
                                                             vaccinations={finalVaccinations.recommendedVaccinations}
                                                             vaccinationType="recommended"/>

                                    }
                                </div>
                            </div>
                        }

                        {
                            finalVaccinations.contraindicatedVaccinations.length > 0 &&
                            <div className="results_vaccinations-header-bar__wrapper">
                                <div className="results_vaccinations-header__wrapper">
                                    {t('contraindicated')}
                                </div>
                                <div className="results_vaccinations-bar__wrapper contraindicated-text contraindicated-background">
                                    <ContraindicatedVaccinationsBar t={t}
                                                                    showResultsForProfessionals={showResultsForProfessionals}
                                                                    vaccinations={finalVaccinations.contraindicatedVaccinations} />
                                </div>
                            </div>
                        }

                        <div className="results_results-disclaimer__wrapper">
                            {t('traveling_disclaimer')}
                        </div>

                        <div className="results_results-disclaimer__wrapper">
                            {t('results_disclaimer')}
                        </div>

                        <div className="results_actions__wrapper">

                            <div className="results_actions-print__wrapper">
                                {showResultsForProfessionals &&
                                    <PDFDownloadLink
                                        document={<Pdf key={pdfKey} t={t} resultsCtx={resultsCtx} finalVaccinations={finalVaccinations} />}
                                        fileName={`${t('pdf_name')}.pdf`}
                                        style={pdfButtonStyle}
                                    >
                                        {t('pdf_full_version')} <Download size={18}/>
                                    </PDFDownloadLink>
                                }

                                <PDFDownloadLink
                                    target="_blank"
                                    document={<PdfShortened key={pdfKey} t={t} finalVaccinations={finalVaccinations} />}
                                    fileName={`${t('pdf_name')}.pdf`}
                                    style={pdfButtonStyle}
                                >
                                    {showResultsForProfessionals ? t('pdf_shortened_version') : t('download_pdf')} <Download size={18}/>
                                </PDFDownloadLink>
                            </div>

                            <div className="results_actions-bibliography__wrapper" onClick={() => navigate('/bibliography')}>
                                <div className="results_actions-button__wrapper">
                                    {t('bibliography')} <ExternalLink size={18}/>
                                </div>
                            </div>

                        </div>

                    </div>

                    <div className="results_buttons__wrapper">

                        <a className="results_link"
                           href="https://form.typeform.com/to/Mtkt24OB"
                           target="_blank"
                           rel="noreferrer">
                            <div className="results_bottom_button results_survey-button">
                                {t('results_survey_button')}
                            </div>
                        </a>
                        <a className="results_link"
                           href="https://form.typeform.com/to/xH7FkzMo"
                           target="_blank"
                           rel="noreferrer">
                            <div className="results_bottom_button results_error-button">
                                {t('results_error_button')}
                            </div>
                        </a>
                    </div>

                    <div className="results_disclaimer__wrapper">
                        <p>{showResultsForProfessionals ? t('professionals_disclaimer_2') : t('patients_disclaimer_2')}</p>
                        <p>{showResultsForProfessionals ? t('professionals_disclaimer_3') : t('patients_disclaimer_3')}</p>
                    </div>

                </div>
            }

        </div>
    )

}

export default Results;
